import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { SwaggerUI } from 'axway-react-ui';

export class EndpointView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onExecute = this.onExecute.bind(this);
		this.onCancel = this.onCancel.bind(this);
		this.onClear = this.onClear.bind(this);
	}

	componentWillUnmount() {
		this.onCancel();
	}

	onExecute(request) {
		const { verb, path } = this.props;
		this.props.onTestAPIExecute(verb, path, request);
	}

	onCancel() {
		const { verb, path } = this.props;
		this.props.onTestAPICancel(verb, path);
	}

	onClear() {
		const { verb, path } = this.props;
		this.props.onTestAPIClear(verb, path);
	}

	render() {
		const { endpoint, method, verb, path, prefix, project } = this.props;
		const testmethod = this.props.testmethod || {};
		const xenabled = method['x-enabled'];
		let heading, header;
		const authType = project.apiPrefixSecurity;
		const apikey = project.apikey;
		if (authType === 'apikey') {
			header = {
				Accept: 'application/json',
				apikey: apikey
			};
			heading = 'Examples';
		} else if (authType === 'basic') {
			header = {
				Accept: 'application/json',
				Authorization: 'Basic ' + Buffer.from(apikey + ':').toString('base64')
			};
			heading = 'Examples';
		} else if (authType === 'none') {
			heading = 'Examples';
		} else {
			heading = `Examples - headers needed for ${authType} auth type not included`;
		}
		const scheme = this.props.endpoint.scheme;
		const selectedScheme = scheme && scheme[0] || 'http';
		const headingIntl = this.intl.formatMsg(heading);
		return (
			<SwaggerUI.Method>
				<SwaggerUI.MethodSummary method={method} />
				<SwaggerUI.MethodDescription method={method} />
				<SwaggerUI.MethodURL
					method={method}
					verb={verb}
					path={path}
					prefix={prefix}
					expressPaths
				/>
				<SwaggerUI.MethodParameters
					heading={this.intl.formatMsg('Query parameters')}
					doc={endpoint}
					verb={verb}
					path={path}
					in="query"
				/>
				<SwaggerUI.MethodParameters
					heading={this.intl.formatMsg('Header parameters')}
					doc={endpoint}
					verb={verb}
					path={path}
					in="header"
				/>
				<SwaggerUI.MethodParameters
					heading={this.intl.formatMsg('Path parameters')}
					doc={endpoint}
					verb={verb}
					path={path}
					in="path"
				/>
				<SwaggerUI.MethodParameters
					heading={this.intl.formatMsg('Body parameters')}
					doc={endpoint}
					verb={verb}
					path={path}
					in={[ 'body', 'formData' ]}
				/>
				<SwaggerUI.MethodResponses method={method} doc={endpoint} />
				{(!xenabled || xenabled.enabled)
					&& <SwaggerUI.MethodTest
						heading={this.intl.formatMsg('Test API')}
						method={method}
						verb={verb}
						path={path}
						doc={endpoint}
						onExecute={this.onExecute}
						onCancel={this.onCancel}
						onClear={this.onClear}
						response={testmethod.error ? testmethod.errorDetail : testmethod.response}
						isExecuting={!!testmethod.executing}
					/>
				}
				<SwaggerUI.MethodExamples
					heading={headingIntl}
					method={method}
					scheme={selectedScheme}
					header={header}
					verb={verb}
					path={path}
					doc={endpoint}
				/>
			</SwaggerUI.Method>
		);
	}
}

EndpointView.propTypes = {
	/**
	 * API endpoint (swagger document)
	 */
	endpoint: PropTypes.object.isRequired,

	/**
	 * Method (from `endpoint` swagger document)
	 */
	method: PropTypes.object.isRequired,

	/**
	 * API verb
	 */
	verb: PropTypes.string.isRequired,

	/**
	 * API path
	 */
	path: PropTypes.string.isRequired,

	/**
	 * Prefix on which the endpoint is listening. Must have a leading /.
	 */
	prefix(props, propName, componentName) {
		if (typeof props[propName] !== 'string') {
			return new Error(
				'Invalid prop prefix supplied to'
				+ ` \`${componentName}\`. prefix must be a string. Validation failed.`
			);
		} else if (props[propName][0] !== '/') {
			return new Error(
				'Invalid prop prefix supplied to'
				+ ` \`${componentName}\`. prefix must have a leading /. Validation failed.`
			);
		}
	},

	/**
	 * Test API Endpoint properties.
	 */
	testmethod: PropTypes.shape({
		/**
		 * The response for the test api call.
		 */
		response: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.object
		]),
		/**
		 * Test is executing.
		 */
		executing: PropTypes.bool,
		/**
		 * Error flag.
		 */
		error: PropTypes.bool,
		/**
		 * Error details.
		 */
		errorDetail: PropTypes.object
	}),
	/**
	 * The callback for the test api execution.
	 */
	onTestAPIExecute: PropTypes.func.isRequired,
	/**
	 * The callback for the test api execution cancelation.
	 */
	onTestAPICancel: PropTypes.func.isRequired,
	/**
	 * The callback for the test api state reset.
	 */
	onTestAPIClear: PropTypes.func.isRequired
};

export default injectIntl(EndpointView);
